import React, { useState } from 'react';
import SecurityTestDetails from './SecurityTestDetails';

/**
 * Security Category Detail Component
 */
const SecurityCategoryDetail = ({ category }) => {
  const [selectedTest, setSelectedTest] = useState(null);
  
  // Map of test names to test IDs
  const testIdMap = {
    'Enable VPC Flow Logs': 'vpc-flow-logs',
    'Enable CloudTrail': 'cloud-trail',
    'Configure 365-day log retention': 'log-retention',
    'Enable S3 server access logs': 's3-server-access-logs',
    'Configure AWS vulnerability scanning': 'vulnerability-scanning',
    'Implement AWS Inspector': 'aws-inspector',
    'MFA on root AWS account': 'mfa-root-account',
    'MFA on IAM users': 'mfa-iam-users',
    'MFA on AWS Organizations Users': 'mfa-org-users',
    'Prevent direct policy attachment to user accounts': 'iam-user-policies',
    'Implement encryption for S3 buckets': 's3-bucket-encryption',
    'Implement encryption for DynamoDB tables': 'dynamodb-encryption',
    'Implement encryption for EBS volumes': 'ebs-encryption',
    'Configure load balancers to redirect HTTP to HTTPS': 'https-redirect',
    'Verify GuardDuty is enabled': 'guard-duty',
    'Verify multi-region CloudTrail is enabled': 'multi-region-cloud-trail',
    'Verify Security Hub is enabled': 'security-hub',
    // Add more test mappings as they are implemented
  };
  
  // Open test details modal
  const openTestDetails = (item) => {
    const testId = testIdMap[item.name];
    if (testId) {
      setSelectedTest({
        id: testId,
        name: item.name,
        category: category.title
      });
    }
  };
  
  // Close test details modal
  const closeTestDetails = () => {
    setSelectedTest(null);
  };

  // Check if this category is marked as coming soon
  const isComingSoon = category.title && category.title.includes('Coming Soon');

  return (
    <div className={`bg-blue-50 rounded-lg p-6 border border-blue-200 mb-8 ${category.className || ''} ${isComingSoon ? 'opacity-50' : ''}`}>
      <h3 className="font-bold text-gray-800 mb-3 flex items-center">
        {category.icon}
        <span className="ml-2">{category.title}</span>
      </h3>
      
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Control
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Checked
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {category.items.map((item, index) => (
              <tr 
                key={index} 
                className={testIdMap[item.name] && !isComingSoon ? "cursor-pointer hover:bg-gray-50" : ""}
                onClick={testIdMap[item.name] && !isComingSoon ? () => openTestDetails(item) : undefined}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.name}
                  {testIdMap[item.name] && !isComingSoon && (
                    <span className="ml-2 text-xs text-blue-600">(Click for details)</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    item.status === 'passed' 
                      ? 'bg-green-100 text-green-800' 
                      : item.status === 'warning' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : item.status === 'not-implemented'
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-red-100 text-red-800'
                  }`}>
                    {item.status === 'not-implemented' 
                      ? 'Coming Soon' 
                      : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.lastChecked}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {!isComingSoon && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-3">Remediation Actions</h3>
          <p className="text-gray-700 mb-4">
            The following actions are recommended to address any failed or warning controls:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-700">
            {category.items.filter(item => item.status === 'failed').map((item, index) => (
              <li key={`failed-${index}`} className="text-red-700">
                <strong>{item.name}:</strong> Immediate remediation required
              </li>
            ))}
            {category.items.filter(item => item.status === 'warning').map((item, index) => (
              <li key={`warning-${index}`} className="text-yellow-700">
                <strong>{item.name}:</strong> Review and address within 7 days
              </li>
            ))}
            {category.items.filter(item => item.status !== 'failed' && item.status !== 'warning' && item.status !== 'not-implemented').length === category.items.length && (
              <li className="text-green-700">All controls in this category are passing. No remediation needed.</li>
            )}
          </ul>
        </div>
      )}
      
      {isComingSoon && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-3">Coming Soon</h3>
          <p className="text-gray-700">
            These security controls are currently in development and will be available in a future update.
          </p>
        </div>
      )}
      
      {/* Test Details Modal */}
      {selectedTest && (
        <SecurityTestDetails
          isOpen={!!selectedTest}
          onClose={closeTestDetails}
          testId={selectedTest.id}
          testName={selectedTest.name}
          category={selectedTest.category}
        />
      )}
    </div>
  );
};

export default SecurityCategoryDetail;
